import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useNavigate, NavLink } from "react-router-dom";
import { put } from '../../utils/api';
import { Form, FormLine, FormItem, FormInput } from '../common/Form';
import useApi from '../../hooks/useApi';
const BRAND_MAX = 100;
//
// Основной компонент для редактирования
//
const ExhibitorEditMain = () => {
	const { t, i18n } = useTranslation();
	const navigate = useNavigate();
	const user = useSelector(state => state.auth.user);
    const exhibitor = useSelector(state => state.exhibitor);
    const [ stand, setStand ] = useState('');
    const [ brand, setBrand ] = useState('');
	const [ weight, setWeight ] = useState('0');
	const [ name_ru, setNameRu ] = useState('');
	const [ name_en, setNameEn ] = useState('');
	const [ diplom_name, setDiplomName ] = useState('');
	const [ highlight, setHighlight ] = useState([]);
    const [ success, setSuccess ] = useState('');
    const [ isSaving, setIsSaving ] = useState(false);
    const [ saveError, setSaveError ] = useState('');
	const [ diplom_date, setDiplomDate ] = useState(null);

    // Установка начальных значений
    useEffect(() => {
        if(!exhibitor) return;
        setStand(exhibitor.stand);
        setBrand(exhibitor.brand);
		setWeight(exhibitor.weight + '');
        setNameRu(exhibitor.name.ru);
        setNameEn(exhibitor.name.en);
		setDiplomName(exhibitor.diplom_name ?? '');
		setHighlight(exhibitor.highlight ? exhibitor.highlight : []);
		// Если есть крайняя дата изменения имени для диплома
		if (exhibitor.event?.diplom_name_change_date) {
			setDiplomDate(new Date(exhibitor.event.diplom_name_change_date.replace(' ', 'T') + ':00+03:00'));
		}
    }, [ exhibitor ]);

    // Отправка данных
	const onSubmit = async () => {
        setIsSaving(true);
        setSaveError('');
        setSuccess('');
		const method = '/exhibitors/' + exhibitor._id;
		const parsedWeight = parseInt(weight);
		const data = {
			name_ru,
			name_en,
			diplom_name: diplom_name + ' ',
			highlight: highlight ? highlight : null,
			stand: stand + ' ',
			brand: brand + ' ',
			weight: isNaN(parsedWeight) ? exhibitor.weight : parsedWeight,
		};
        const result = await put(method, data);
        if(result.error) {
            setSaveError(result.error);
        } else {
            setSuccess('exhibitoredit.success');
			navigate('/cabinet/exhibitorview/' + exhibitor._id + '/action/updated');
        }
        setIsSaving(false);
	};

	const getDiplomDatePlaceholder = () => {
		if (!diplom_date) return t('exhibitoredit.diplom_name_hint_until');
		const by = i18n.resolvedLanguage === 'en' ? ' by ' : ' до '; 
		const locale = i18n.resolvedLanguage === 'en' ? 'en-US' : 'ru-RU'; 
		return by +
			diplom_date.toLocaleString(locale, { day: 'numeric', month: 'long'}) + ', ' +
			diplom_date.toLocaleString(locale, { hour: '2-digit', minute: '2-digit' });
	};

    return (
		<div>
	    	<h1>{t('exhibitoredit.title_main')}</h1>
			{isSaving ? <span>{t('common.loading')}</span> :
			<Form 
				success={success}
				error={saveError}
				info='exhibitoredit.subtitle'
				wide={true}
				submit={t('common.save_changes')}
				onSubmit={onSubmit}
			>
				<FormLine>
					<FormItem>
						<FormInput
							id='name_ru'
							type='text'
							value={name_ru}
							onChange={setNameRu}
							placeholder={t('exhibitoredit.name_ru')}
							className='lang-ru'
							label={t('exhibitoredit.name_ru')}
							hint={t('exhibitoredit.name_hint')}
							required={true}
						/>
					</FormItem>
					<FormItem>
						<FormInput
							id='name_en'
							type='text'
							value={name_en}
							onChange={setNameEn}
							placeholder={t('exhibitoredit.name_en')}
							className='lang-en'
							label={t('exhibitoredit.name_en')}
							hint={t('exhibitoredit.name_hint')}
							required={true}
						/>
					</FormItem>
				</FormLine>

				<FormLine>
					<FormItem>
						<FormInput
							id='stand'
							type='text'
							value={stand}
							onChange={setStand}
							placeholder={t('exhibitoredit.stand')}
							label={t('exhibitoredit.stand')}
							hint={t('exhibitoredit.stand_hint')}
							disabled={user.role === 'exhibitor'}
						/>
					</FormItem>
					<FormItem>
						<FormInput
							id='brand'
							type='text'
							value={brand}
							onChange={b => setBrand(b.substring(0, BRAND_MAX))}
							placeholder={t('exhibitoredit.brand')}
							label={t('exhibitoredit.brand')}
							hint={t('exhibitoredit.brand_hint') + ' (' + brand.length + '/' + BRAND_MAX + ')'}
						/>
					</FormItem>
				</FormLine>

				<FormLine>
					<FormItem>
						<FormInput
							id='diplom_name'
							type='text'
							value={diplom_name}
							onChange={setDiplomName}
							placeholder={t('exhibitoredit.diplom_name_placeholder')}
							label={t('exhibitoredit.diplom_name')}
							hint={t('exhibitoredit.diplom_name_hint', { until: getDiplomDatePlaceholder()})}
							disabled={diplom_date ? (user.role !== 'root' && diplom_date.getTime() <= Date.now()) : false}
							required={user.role === 'exhibitor' || user.role === 'agent'}
						/>
					</FormItem>
					{/* Вес для сортировки может устанавливать только администратор */}
					{(user.role === 'root' || user.role === 'admin') &&
					<FormItem>
						<FormInput
							id='weight'
							type='text'
							value={weight}
							onChange={b => setWeight(b)}
							placeholder={t('exhibitoredit.weight')}
							label={t('exhibitoredit.weight')}
							hint={t('exhibitoredit.weight_hint')}
						/>
					</FormItem>}
				</FormLine>

				{/* Подсветку может устанавливать только администратор */}
				{(user.role === 'root' || user.role === 'admin') &&
				<FormLine>
					<FormItem>
						<ExhibitorSetHighlight
							exhibitor={exhibitor}
							onSetHighlight={setHighlight}
						/>
					</FormItem>
				</FormLine>}

			</Form>}
			
			<NavLink to={'/cabinet/exhibitorview/' + exhibitor._id}>
				{t('exhibitoredit.back')}
			</NavLink>
		</div>
	);
};
//
// Вспомогательный компонент для установки highlight
// Отображает список чекбоксов из основных категорий.
// Запрашивает список экспонентов которые уже назначены топами в этих категориях
// и запрещает выбирать занятые категории.
//
const ExhibitorSetHighlight = ({ exhibitor, onSetHighlight }) => {
	const { t, i18n } = useTranslation();
	const lang = i18n.language;
	const method = '/exhibitors_in_highlights';
	const [ highlight, setHighlight ] = useState([]);
	const [ categories, setCategories ] = useState([]);
	const [ type, setType ] = useState('unknown');
	const [ params, setParams ] = useState([]);
	// eslint-disable-next-line
	const [ data, error, isLoading ] = useApi({ type, method, params });

    // Установка начальных значений
    useEffect(() => {
        if(!exhibitor) return;
		setHighlight(exhibitor.highlight ? exhibitor.highlight : []);
		// Подсветку делаем только среди основных категорий
		const categories = exhibitor.categories.filter(c => c.type === 'main');
		setCategories(categories);
		// Для запроса занятых категорий формируем их список и отправляем запрос
		const cats = categories.map(i => i._id);
		setParams({ categories: cats.join() });
		setType('get');		
    }, [ exhibitor ]);

	// Выбран или нет элемент
	const isChecked = (id) => {
		return highlight.includes(id);
	};

	// При отрисовке запрещаем к выбору уже занятые категории
	const isDisabled = (id) => {
		// Если нет данных или была ошибка, все запрещено к выбору
		if(!data || error) return true;
		// Ищем экспонента у которого уже выбрана эта категория
		const exp = data.list.find(i => i.highlight.includes(id));
		if(exp === undefined) {
			return false;
		} else {
			// Экспонент нашелся, но это можем быть мы сами
			return !(exp._id === exhibitor._id);
		}
	};

	// Установка подсветки
	const onChange = (id) => {
		const hl = [...highlight];
		const index = hl.indexOf(id);
		if(index >= 0) hl.splice(index, 1); else hl.push(id);
		setHighlight(hl);
		onSetHighlight(hl);
	};

	if(!exhibitor) return null;
	if(isLoading) return <p>{t('common.loading')}</p>;

	return (
		<fieldset id='highlight'>
			<legend>{t('exhibitoredit.highlight')}</legend>
			{!categories.length ? <div>{t('exhibitoredit.nocategories')}</div> :
			<React.Fragment>
				{categories.map(item => (
					<div className='Form-row' key={item._id}>
						<input
							id={item._id}
							type='checkbox'
							value={item._id}
							name={`highlight-${item._id}`}
							checked={isChecked(item._id)}
							disabled={isDisabled(item._id)}
							onChange={e => onChange(e.target.value)}
						/>
						<label
							htmlFor={item._id}
							className={isDisabled(item._id) ? 'disabled' : null}
						>
							{item.name[lang]}
						</label><br/>
					</div>
				))}
			</React.Fragment>}
			<strong>{t('exhibitoredit.highlight_hint')}</strong>
		</fieldset>	
	);
};

export default ExhibitorEditMain;
